import { ActivityType } from '@showbie-socrative/socrative-utils';
import { z } from 'zod/v4';

import { LaunchRoute } from '~teacher/launchv2/context/LaunchModalContext';

import axios from '../localAxios';
import { startQuizUrl } from './startQuizURLs';

const createLaunchQuizBody = ({
  quizId,
  settings: {
    pacing,
    randomAnswers,
    randomQuestions,
    oneAttempt,
    requireNames,
    showFeedback,
    showResults,
    allowStudentNav,
  },
  roomId,
  isShowbieActivity,
}: CreateLaunchQuizBodyParams) => ({
  ...(roomId ? { room_id: roomId } : {}),
  quiz_id: quizId,
  is_showbie_activity: isShowbieActivity,
  settings: {
    show_results: showResults,
    show_feedback: showFeedback,
    one_attempt: oneAttempt,
    require_names: requireNames,
    random_questions: randomQuestions,
    random_answers: randomAnswers,
    allow_student_nav: allowStudentNav,
    pacing: pacing,
  },
});

const createLaunchWorkspaceQuizBody = ({
  workspaceId,
  workspaceQuizId,
  settings: {
    pacing,
    randomAnswers,
    randomQuestions,
    oneAttempt,
    requireNames,
    showFeedback,
    showResults,
    allowStudentNav,
  },
  roomId,
  isShowbieActivity,
}: CreateLaunchQuizBodyParams) => ({
  ...(roomId ? { room_id: roomId } : {}),
  is_showbie_activity: isShowbieActivity,
  workspace_data: {
    workspace_id: workspaceId,
    workspace_quiz_id: workspaceQuizId,
  },
  settings: {
    show_results: showResults,
    show_feedback: showFeedback,
    one_attempt: oneAttempt,
    require_names: requireNames,
    random_questions: randomQuestions,
    random_answers: randomAnswers,
    allow_student_nav: allowStudentNav,
    pacing: pacing,
  },
});

interface QuizSettings {
  allowStudentNav: boolean;
  oneAttempt: boolean;
  pacing: 'student' | 'teacher';
  randomAnswers: boolean;
  randomQuestions: boolean;
  requireNames: boolean;
  showFeedback: boolean;
  showResults: boolean;
}

export interface StartQuizParams extends CreateLaunchQuizBodyParams {
  launchRoute: LaunchRoute;
  quizType: 'Workspace' | 'Quiz';
}

export interface CreateLaunchQuizBodyParams {
  roomId: number;
  roomHasRoster: boolean;
  quizId: number;
  quizName: string;
  workspaceQuizId: string;
  workspaceId: string;
  settings: QuizSettings;
  isShowbieActivity: boolean;
}
export async function startQuiz({ quizType, ...params }: StartQuizParams) {
  const data =
    quizType === 'Workspace'
      ? createLaunchWorkspaceQuizBody(params)
      : createLaunchQuizBody(params);
  const url = startQuizUrl();
  const result = await axios.post<StartQuizResponse>(url.href, data);

  return startQuizResponseSchema.parse(result.data);
}
type StartQuizResponse = z.infer<typeof startQuizResponseSchema>;

const quizSchema = z.object({
  createdById: z.number(),
  id: z.number(),
  sharable: z.boolean(),
  isHidden: z.boolean(),
  createdDate: z.string(),
  name: z.string(),
  lastUpdated: z.string(),
  deleted: z.boolean(),
  socNumber: z.string(),
  version: z.number(),
  parentId: z.number().optional().nullable(),
});

const settingKeySchema = z.union([
  z.literal('allow_student_nav'),
  z.literal('one_attempt'),
  z.literal('random_answers'),
  z.literal('random_questions'),
  z.literal('require_names'),
  z.literal('show_feedback'),
  z.literal('show_results'),
]);

const settingsSchema = z.array(
  z.union([
    z.object({ key: settingKeySchema, value: z.boolean() }),
    z.object({ key: z.literal('pacing'), value: z.string() }),
  ])
);

export const startQuizResponseSchema = z.object({
  startedById: z.number(),
  settings: settingsSchema,
  id: z.number(),
  activityType: z.enum(ActivityType),
  type: z.string(),
  activityId: z.number(),
  activityState: z.number(),
  startTime: z.number(),
  endTime: z.string().nullable(),
  hideReport: z.boolean(),
  studentNames: z.array(z.string()),
  studentsFinished: z.array(z.string()),
  responses: z.array(z.unknown()),
  name: z.string(),
  quiz: quizSchema,
});
